import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import Typography from '@material-ui/core/Typography';
import SyncIcon from '@material-ui/icons/Sync';
import {
  ApiFilterOperation,
  DetailActionButton,
  DetailContext,
  DetailHandle,
  DetailMode,
  FormContext,
  ResultDto,
  TableFieldToolbarButton,
} from '@eas/common-web';
import { padIcoNumberWithZeros } from '@composite/contact/contact-schema';
import {
  BusinessNaturalPerson,
  Contact,
  DictContactType,
  NaturalPerson,
  Subject,
} from '@models';
import { EvidenceApiUrl, SubjectType } from '@enums';
import { IdentifyDialog } from './identify-dialog';

export function IdentifyButton({
  disabled,
  selectedContact,
}: {
  disabled?: boolean;
  selectedContact?: Contact;
}) {
  const intl = useIntl();
  const { source, refreshAll, cancelEditing } =
    useContext<DetailHandle<Subject>>(DetailContext);
  const { getFieldValues } = useContext(FormContext);

  return (
    <>
      <DetailActionButton
        promptKey="IDENTIFY_CONTACT"
        submitCallback={async ({ selectedSubject }) => {
          const {
            subjectType,
            deliveryAddress,
            address,
            email,
            phoneNumber,
            ...subject
          } = selectedSubject;

          let deliveryAddressType: DictContactType | undefined;
          let mainContactType: DictContactType | undefined;

          if (deliveryAddress || address) {
            const contactTypesResult = (await fetch(
              `${EvidenceApiUrl.SUBJECT_CONTACTS}/type/list`,
              {
                method: 'POST',
                headers: new Headers({
                  'Content-Type': 'application/json',
                }),
                body: JSON.stringify({
                  filters: [
                    {
                      operation: ApiFilterOperation.OR,
                      filters: [
                        {
                          operation: ApiFilterOperation.CONTAINS,
                          field: 'name',
                          value: 'Doručovací adresa',
                        },
                        {
                          operation: ApiFilterOperation.CONTAINS,
                          field: 'name',
                          value: 'Hlavní kontakt',
                        },
                        {
                          operation: ApiFilterOperation.EQ,
                          field: 'isDefault',
                          value: true,
                        },
                      ],
                    },
                  ],
                }),
              }
            ).then((r) => r.json())) as ResultDto<
              DictContactType & { default: boolean }
            >;

            if (contactTypesResult.count > 0) {
              const items = contactTypesResult.items ?? [];

              if (deliveryAddress) {
                deliveryAddressType = items.find((item) =>
                  item?.name?.includes?.('Doručovací adresa')
                );

                if (!deliveryAddressType) {
                  deliveryAddressType = items.find((item) => item.default);
                }
              }

              if (address) {
                mainContactType = items.find((item) =>
                  item?.name?.includes?.('Hlavní kontakt')
                );
              }
            }
          }

          const contacts = (source?.data?.contacts ?? []).filter(
            (c: Contact) => c.id !== selectedContact?.id
          );

          // special case when we do not want to loose filled info
          // https://inqool.atlassian.net/browse/USCR-2225?focusedCommentId=19048
          const nonNullSelectedContact = Object.fromEntries(
            Object.entries(selectedContact || {}).filter(
              ([, value]) => value !== null
            )
          );

          const newContacts = [
            ...contacts,
            {
              ...nonNullSelectedContact,
              address,
              email,
              phone: phoneNumber,
              ...(mainContactType ? { type: mainContactType } : {}),
            },
          ];

          if (deliveryAddress) {
            newContacts.push({
              ...nonNullSelectedContact,
              id: uuidv4(),
              address: deliveryAddress,
              email,
              phone: phoneNumber,
              ...(deliveryAddressType ? { type: deliveryAddressType } : {}),
            });
          }

          await source.update(
            {
              ...source.data,
              ...getFieldValues(),
              type: subjectType,
              ...subject,
              contacts: newContacts,
            },
            source.data!
          );

          refreshAll();
          cancelEditing();
        }}
        ButtonComponent={({ onClick, label }) => (
          <TableFieldToolbarButton
            IconComponent={SyncIcon}
            show={true}
            disabled={disabled || false}
            title={label}
            onClick={onClick}
            color="default"
          >
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'ES__CONTACTS_TOOLBAR__IDENTIFY_CONTACT__BUTTON',
                defaultMessage: 'Ztotožnit',
              })}
            </Typography>
          </TableFieldToolbarButton>
        )}
        buttonLabel={intl.formatMessage({
          id: 'ES__CONTACTS__IDENTIFY_CONTACT__BUTTON',
          defaultMessage: 'Ztotožnit subjekt',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__CONTACTS__IDENTIFY_CONTACT__TITLE',
          defaultMessage: 'Ztotožnění subjektu',
        })}
        dialogText={intl.formatMessage(
          {
            id: 'ES__CONTACTS__IDENTIFY_CONTACT__TEXT',
            defaultMessage:
              'Ztotožnění proběhlo na základě těchto údajů:{br}{values}Vyberte subjekt, jehož údaje chcete převzít.',
          },
          {
            br: <br />,
            values:
              getFieldValues()?.type === SubjectType.NATURAL_PERSON ? (
                <div style={{ marginLeft: 15, marginBottom: 10 }}>
                  - 1. Jméno, příjmení, datum narození a kód adresního místa:{' '}
                  <b>
                    {[
                      (getFieldValues() as NaturalPerson).firstName,
                      (getFieldValues() as NaturalPerson).lastName,
                      (getFieldValues() as NaturalPerson).birthDate
                        ? new Date(
                            (getFieldValues() as NaturalPerson).birthDate!
                          ).toLocaleDateString()
                        : null,
                      selectedContact?.address?.locationCode,
                    ]
                      .filter((v) => v)
                      .join(', ')}
                  </b>
                  <br />- 2. Číslo DS:{' '}
                  <b>{selectedContact?.dataBox?.identifier}</b>
                </div>
              ) : getFieldValues()?.type ===
                SubjectType.BUSINESS_NATURAL_PERSON ? (
                <div style={{ marginLeft: 15, marginBottom: 10 }}>
                  - 1. Jméno, příjmení, datum narození a kód adresního místa
                  sídla:
                  <b>
                    {[
                      (getFieldValues() as BusinessNaturalPerson)?.firstName,
                      (getFieldValues() as BusinessNaturalPerson)?.lastName,
                      (getFieldValues() as BusinessNaturalPerson)?.birthDate
                        ? new Date(
                            (
                              getFieldValues() as BusinessNaturalPerson
                            ).birthDate!
                          ).toLocaleDateString()
                        : null,
                      selectedContact?.address?.locationCode,
                    ]
                      .filter((v) => v)
                      .join(', ')}
                  </b>
                  <br />- 2. Číslo DS:{' '}
                  <b>{selectedContact?.dataBox?.identifier}</b>
                  <br />- 3. IČO:{' '}
                  <b>{(getFieldValues() as BusinessNaturalPerson)?.ico}</b>
                  <br />- 4. Název:{' '}
                  <b>
                    {(getFieldValues() as BusinessNaturalPerson)?.companyName}
                  </b>
                </div>
              ) : getFieldValues()?.type === SubjectType.LEGAL_ENTITY ? (
                <div style={{ marginLeft: 15, marginBottom: 10 }}>
                  - 1. Číslo DS: <b>{selectedContact?.dataBox?.identifier}</b>
                  <br />- 2. IČO:{' '}
                  <b>{(getFieldValues() as BusinessNaturalPerson)?.ico}</b>
                  <br />- 3. Název firmy:{' '}
                  <b>
                    {(getFieldValues() as BusinessNaturalPerson)?.companyName}
                  </b>
                </div>
              ) : (
                <div style={{ marginLeft: 15, marginBottom: 10 }}>
                  - 1. IČO:{' '}
                  <b>{(getFieldValues() as BusinessNaturalPerson)?.ico}</b>
                  <br />- 2. Číslo DS:{' '}
                  <b>{selectedContact?.dataBox?.identifier}</b>
                  <br />- 3. Jméno, příjmení, datum narození:{' '}
                  <b>
                    {[
                      (getFieldValues() as NaturalPerson)?.firstName,
                      (getFieldValues() as NaturalPerson)?.lastName,
                      (getFieldValues() as NaturalPerson)?.birthDate
                        ? new Date(
                            (getFieldValues() as NaturalPerson).birthDate!
                          ).toLocaleDateString()
                        : null,
                    ]
                      .filter((v) => v)
                      .join(', ')}
                  </b>
                </div>
              ),
          }
        )}
        dialogWidth={1100}
        modes={[DetailMode.NEW, DetailMode.EDIT, DetailMode.VIEW]}
        FormFields={() => (
          <IdentifyDialog
            contact={selectedContact}
            subject={{
              ...(getFieldValues() as Subject),
              ico: padIcoNumberWithZeros(getFieldValues()?.ico),
            }}
          />
        )}
      />
    </>
  );
}
